/**
 * How the signature on a training enrollment record was produced, so an auditor
 * can always tell a reused signature from a freshly drawn one (WI 31501).
 * `DRAWN` = signed fresh on this sign-in; `REUSED` = a stored signature applied
 * after the attendee re-confirmed it. The backend defaults to `DRAWN` when the
 * flag is omitted.
 */
export type SignatureSource = 'DRAWN' | 'REUSED';

export interface TrainingEnrollmentEmployee {
  hrRef: number;
  absorbUserId: string | null;
}

export interface TrainingEnrollmentContext {
  trainingId: string | null;
  absorbCourseId: string;
  trainingDate: string;
  facilityId: number;
  /** Viewpoint facility KeyID when the picker is Viewpoint-backed; see resolveEnrollmentFacility. */
  vpParentFacilityKeyID?: number | null;
  instructorHRRef: number;
  jobIds: string[];
}

/** A picked facility row, from either the Sparta or the Viewpoint facility list. */
export interface PickedFacility {
  ParentFacilityID?: number | null;
  VPParentFacilityKeyID?: number | null;
}

/**
 * Decides which identifier an enrollment request should carry for the picked facility (#33872).
 *
 * The two facility lists return different number spaces under the same property: the Sparta list
 * puts its own `ParentFacility` PK in `ParentFacilityID`, while the Viewpoint list puts
 * `budFacility.KeyID` there (so `ParentFacilityID === VPParentFacilityKeyID` on a Viewpoint row).
 * Sending a Viewpoint key as `facilityId` is what produced the prod incident: it either violated
 * FK_Training_ParentFacility as an opaque 500, or collided with an unrelated Sparta facility and
 * recorded the training against the wrong site.
 *
 * So the caller states which list it is pointed at rather than guessing from the row, and the server
 * resolves a Viewpoint key through the Sparta mirror. Never send both: a Sparta row also carries a
 * Viewpoint key, and resolving that key could land on a different facility than the user picked when
 * one Viewpoint facility is mirrored by several Sparta rows.
 */
export function resolveEnrollmentFacility(
  facility: PickedFacility | null | undefined,
  isViewpointBacked: boolean
): { facilityId: number; vpParentFacilityKeyID: number | null } {
  const pickedId = facility?.ParentFacilityID ?? 0;

  if (isViewpointBacked) {
    return {
      facilityId: 0,
      vpParentFacilityKeyID:
        facility?.VPParentFacilityKeyID ?? (pickedId || null),
    };
  }

  return { facilityId: pickedId, vpParentFacilityKeyID: null };
}

export interface TrainingEnrollmentRequest {
  trainingId: string | null;
  hRRef: number;
  absorbUserId: string | null;
  signedOn: string | null;
  signatureBase64: string | null;
  bActive: boolean;
  absorbCourseId: string;
  trainingDate: string;
  facilityId: number;
  /** Viewpoint facility KeyID the server resolves to the Sparta facility (#33872); null on a Sparta-backed pick. */
  vpParentFacilityKeyID: number | null;
  jobIds: string[];
  instructorHRRef: number;
  /** DRAWN | REUSED audit flag; null lets the backend default to DRAWN. */
  signatureSource: SignatureSource | null;
}

export function isSignedOnDate(
  signedOn: Date | string | null | undefined,
  trainingDate: string | null | undefined
): boolean {
  if (!signedOn || !trainingDate) {
    return false;
  }

  const signedDate = new Date(signedOn);
  const trainingDateObj = new Date(trainingDate);

  return (
    signedDate.getFullYear() === trainingDateObj.getFullYear() &&
    signedDate.getMonth() === trainingDateObj.getMonth() &&
    signedDate.getDate() === trainingDateObj.getDate()
  );
}

export function buildEnrollmentRequest(
  employee: TrainingEnrollmentEmployee,
  context: TrainingEnrollmentContext,
  signature?: {
    signedOn: string;
    signatureBase64: string;
    signatureSource?: SignatureSource;
  }
): TrainingEnrollmentRequest {
  return {
    trainingId: context.trainingId,
    hRRef: employee.hrRef,
    absorbUserId: employee.absorbUserId,
    signedOn: signature?.signedOn ?? null,
    signatureBase64: signature?.signatureBase64 ?? null,
    bActive: true,
    absorbCourseId: context.absorbCourseId,
    trainingDate: context.trainingDate,
    facilityId: context.facilityId,
    vpParentFacilityKeyID: context.vpParentFacilityKeyID ?? null,
    jobIds: context.jobIds,
    instructorHRRef: context.instructorHRRef,
    // Only a signed request carries a source; the enroll-only path leaves it
    // null so the backend applies its DRAWN default when a signature lands later.
    signatureSource: signature ? (signature.signatureSource ?? 'DRAWN') : null,
  };
}
